import React, { useEffect, useState } from 'react'
import { VERDICT_LABEL } from '@treu/shared'

export type VerdictTone = 'true' | 'mixed' | 'unverified' | 'false'

const TONE_VAR: Record<VerdictTone, string> = {
  true: '--tr-verdict-true',
  mixed: '--tr-verdict-mixed',
  unverified: '--tr-verdict-unverified',
  false: '--tr-verdict-false',
}

const TONE_VAR_DARK: Record<VerdictTone, string> = {
  true: '--tr-verdict-true-300',
  mixed: '--tr-verdict-mixed-300',
  unverified: '--tr-verdict-unverified-300',
  false: '--tr-verdict-false-300',
}

export type ConfidenceBand = 'high' | 'moderate' | 'low'

/** Bands the analysis-confidence score on a green / amber / red scale. */
export function confidenceBand(confidence: number): ConfidenceBand {
  if (confidence >= 80) return 'high'
  if (confidence >= 60) return 'moderate'
  return 'low'
}

const BAND_TONE: Record<ConfidenceBand, VerdictTone> = {
  high: 'true',
  moderate: 'unverified',
  low: 'false',
}

export const CONFIDENCE_TEXT_CLASS: Record<ConfidenceBand, string> = {
  high: 'text-verdict-true',
  moderate: 'text-verdict-unverified',
  low: 'text-verdict-false',
}

export const CONFIDENCE_TEXT_CLASS_DARK: Record<ConfidenceBand, string> = {
  high: 'text-verdict-true-300',
  moderate: 'text-verdict-unverified-300',
  low: 'text-verdict-false-300',
}

export const CONFIDENCE_BAND_LABEL: Record<ConfidenceBand, string> = {
  high: 'High confidence — the evidence resolved this claim clearly',
  moderate: 'Moderate confidence — the evidence is partial or mixed',
  low: 'Low confidence — the evidence was thin or conflicting',
}

/** Maps an analysis-confidence score to its tone (fallback when no verdict). */
export function toneForConfidence(confidence: number): VerdictTone {
  return BAND_TONE[confidenceBand(confidence)]
}

/** Backend verdict enum (packages/shared fact-check.ts). */
export type BackendVerdict =
  | 'strongly_supported'
  | 'largely_supported'
  | 'balanced'
  | 'limited_support'
  | 'insufficient'

export function toneForVerdict(verdict: BackendVerdict): VerdictTone {
  switch (verdict) {
    case 'strongly_supported':
    case 'largely_supported':
      return 'true'
    case 'balanced':
      return 'mixed'
    case 'limited_support':
    case 'insufficient':
      return 'unverified'
  }
}

// Canonical noun-phrase labels, re-exported from @treu/shared so existing
// consumers keep importing from here. Do NOT redeclare locally — a diverged
// copy in api/verdict.ts once made link previews disagree with the page.
export { VERDICT_LABEL }

interface VerdictSealProps {
  /** Confidence 0–100, or null to render a numberless – seal */
  confidence: number | null
  tone: VerdictTone
  /** Diameter in px */
  size?: number
  /** Renders track/ink for dark surfaces */
  onDark?: boolean
}

/**
 * The Verdict Seal — Treu's signature object. The arc shows the engine's
 * confidence (how clearly the claim was resolved), colored on the
 * green / amber / red confidence scale; the verdict tone carries the
 * separate support-for-the-claim signal in the label beside it.
 * `confidence: null` renders a numberless seal (empty track, – centered),
 * which falls back to `tone`.
 */
export const VerdictSeal: React.FC<VerdictSealProps> = ({ confidence, tone, size = 64, onDark = false }) => {
  const clamped = confidence === null ? null : Math.max(0, Math.min(100, Math.round(confidence)))
  const strokeWidth = Math.max(3, size * 0.07)
  const r = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * r
  const [offset, setOffset] = useState(circumference)

  // Draw the arc in on mount / when the score changes.
  useEffect(() => {
    const id = requestAnimationFrame(() =>
      setOffset(circumference * (1 - (clamped ?? 0) / 100))
    )
    return () => cancelAnimationFrame(id)
  }, [clamped, circumference])

  const colorTone = clamped === null ? tone : BAND_TONE[confidenceBand(clamped)]
  const color = `rgb(var(${onDark ? TONE_VAR_DARK[colorTone] : TONE_VAR[colorTone]}))`

  return (
    <div
      className="relative inline-flex items-center justify-center flex-shrink-0"
      style={{ width: size, height: size }}
      role="img"
      aria-label={
        clamped === null
          ? 'Insufficient evidence'
          : `${clamped}% confidence in our analysis — ${CONFIDENCE_BAND_LABEL[confidenceBand(clamped)]}`
      }
      title={clamped === null ? undefined : CONFIDENCE_BAND_LABEL[confidenceBand(clamped)]}
    >
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={strokeWidth}
          className={onDark ? 'stroke-white/15' : 'stroke-paper-200'}
        />
        {clamped !== null && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            stroke={color}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            style={{ transition: 'stroke-dashoffset 900ms cubic-bezier(0.22, 1, 0.36, 1)' }}
          />
        )}
      </svg>
      <span
        className="absolute font-display font-semibold tabular-nums"
        style={{ color, fontSize: size * 0.26 }}
      >
        {clamped === null ? '–' : `${clamped}%`}
      </span>
    </div>
  )
}

export default VerdictSeal
